const { loadStoriesPlayer } = require('./bundle');
const { onElementInViewport } = require('../viewportObserver');
const { logError } = require('../logger');
const { reportSdkDiagnostic } = require('../sdkDiagnostics');
const {
  DEFAULT_VAST_PRE_ROLL_TAG,
  DEFAULT_VAST_INTERSTITIAL_TAG
} = require('./constants');

function shouldSkipStoriesPlayer(ctx) {
  const { _noads, _urlonly, _sponsored, pro } = ctx || {};
  return _sponsored === 'yes' || pro === 'active' || _noads === 'yes' || _urlonly === 'yes';
}

function createStoriesPlayer(options = {}) {
  const baseOptions = {
    language: options.language || 'en',
    brand: options.brand || null,
    playlist: options.playlist || null,
    manifestUrl: options.manifestUrl,
    vastPreRollTag: options.vastPreRollTag || DEFAULT_VAST_PRE_ROLL_TAG,
    vastInterstitialTag: options.vastInterstitialTag || DEFAULT_VAST_INTERSTITIAL_TAG,
    resolveAdTag: typeof options.resolveAdTag === 'function' ? options.resolveAdTag : null,
    onVideoAd: typeof options.onVideoAd === 'function' ? options.onVideoAd : null,
    analyticsEndpoint: options.analyticsEndpoint || null,
    analyticsFlushIntervalMs: options.analyticsFlushIntervalMs,
    analyticsHeartbeatIntervalMs: options.analyticsHeartbeatIntervalMs,
    analyticsMaxBatchSize: options.analyticsMaxBatchSize,
    analyticsInactivityTimeoutMs: options.analyticsInactivityTimeoutMs,
    initialPlaybackThresholdPx: options.initialPlaybackThresholdPx,
    initialPlaybackRootMargin: options.initialPlaybackRootMargin,
    containerSelector: options.containerSelector || '.stories-player-container',
    proLinkUrl: options.proLinkUrl || null,
    proLinkText: options.proLinkText || null,
    loop: options.loop,
    showAware: options.showAware === true,
    contentEnabled: options.contentEnabled !== false,
    adsEnabled: options.adsEnabled !== false,
    clips: options.clips,
    startVideoId: options.startVideoId
  };

  function initStoriesPlayerForTarget(target, ctx) {
    if (!target || target.__millaStoriesPlayerInstance || target.__millaStoriesPlayerPending) {
      return;
    }
    target.__millaStoriesPlayerPending = true;
    loadStoriesPlayer().catch((err) => {
      reportSdkDiagnostic('stories_bundle_failed');
      throw err;
    }).then(({ StoriesPlayerCore }) => {
      // The container can leave the page (infinite scroll) while the bundle loads.
      if (target.isConnected === false) return;
      reportSdkDiagnostic('stories_player');
      const player = new StoriesPlayerCore({
        onDiagnostic: reportSdkDiagnostic,
        ...baseOptions,
        adsEnabled: baseOptions.adsEnabled && ctx?.pro !== 'active',
        container: target,
        brand: baseOptions.brand || null,
        language: baseOptions.language || (ctx && ctx._fr_lang === 'fr' ? 'fr' : 'en'),
        playlist: baseOptions.playlist,
        resolveAdTag: baseOptions.resolveAdTag,
        analyticsEndpoint: baseOptions.analyticsEndpoint || undefined,
        analyticsFlushIntervalMs: baseOptions.analyticsFlushIntervalMs,
        analyticsHeartbeatIntervalMs: baseOptions.analyticsHeartbeatIntervalMs,
        analyticsMaxBatchSize: baseOptions.analyticsMaxBatchSize,
        analyticsInactivityTimeoutMs: baseOptions.analyticsInactivityTimeoutMs,
        initialPlaybackThresholdPx: baseOptions.initialPlaybackThresholdPx,
        initialPlaybackRootMargin: baseOptions.initialPlaybackRootMargin
      });

      target.__millaStoriesPlayerInstance = player;
      const initResult = player.init();
      if (initResult && typeof initResult.catch === 'function') {
        initResult.catch((err) => logError('Stories player init failed', err));
      }
    }).catch((err) => {
      logError('Stories player init failed', err);
    }).finally(() => {
      target.__millaStoriesPlayerPending = false;
    });
  }

  return function initOnPage(ctx = {}) {
    const skip = !baseOptions.contentEnabled || (baseOptions.showAware
      ? shouldSkipStoriesPlayer({ ...ctx, pro: undefined })
      : shouldSkipStoriesPlayer(ctx));
    if (skip) {
      return;
    }

    // Existing and later containers alike start near the viewport; the
    // observer also covers the ones already in the page when it is set up.
    onElementInViewport({
      selector: baseOptions.containerSelector,
      onIntersect: function onStoriesPlayerIntersect(entry) {
        const target = entry && entry.target;
        initStoriesPlayerForTarget(target, ctx);
      },
      rootMargin: '1200px 0px 1200px 0px',
      oneTime: true
    });
  };
}

module.exports = {
  createStoriesPlayer
};
